import styled, { createGlobalStyle } from "styled-components";
import { useRef, useEffect } from "react";
import { usePage, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import useReducedMotion from "../_kit/useReducedMotion";
import SkipLink from "../_kit/SkipLink";
import LanguageSwitch from "../_kit/LanguageSwitch";
import COPY from "./copy";
import {
  propertyHome,
  insightsHome,
  guestPortal,
} from "../../../components/insights/paths";
import {
  z,
  weight,
  type,
  tracking,
  space,
  radius,
  motion,
  media,
  leading,
  font,
  color,
} from "../../../styles/tokens";

/* The HostelNMC brand page at "/".
 *
 * One screen: the wordmark, what we are, and the ways in. It is a threshold rather than a
 * destination — the property page is where someone actually decides something — so it earns exactly
 * one viewport and no scrolling.
 *
 * The video is served from public/, NOT the CMS. Everything on this page is static on purpose (see
 * app/services/hebe/brand.rb): that is why "/" stayed up at 200 when Strapi went down and every
 * other marketing page returned 503. Sourcing the hero from Strapi would hand that back.
 */

const PageStyle = createGlobalStyle`
  body {
    background: ${color.ink};
  }
`;

const Screen = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  /* min-height, not height: at 320px on a short viewport the content is taller than the screen and
   * must be allowed to scroll rather than be clipped. */
  min-height: 100dvh;
  padding: clamp(88px, 12vh, 140px) clamp(20px, 5vw, 40px)
    clamp(32px, 6vh, 64px);
  overflow: hidden;
  isolation: isolate;
  color: ${color.onDark};
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;

  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background: ${color.scrim};
`;

const Inner = styled.div`
  position: relative;
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  z-index: ${z.base};
`;

const Eyebrow = styled.p`
  margin: 0 0 ${space[4]};
  font-family: ${font.body};
  font-size: ${type.eyebrow};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.eyebrow};
  text-transform: uppercase;
  color: ${color.gold};
`;

const Wordmark = styled.h1`
  margin: 0 0 ${space[4]};
  font-family: ${font.display};
  font-size: clamp(2.75rem, 1.4rem + 6vw, 5.5rem);
  font-weight: ${weight.regular};
  line-height: 0.95;
  letter-spacing: ${tracking.tight};

  span {
    color: ${color.gold};
  }
`;

const Tagline = styled.p`
  max-width: 30ch;
  margin: 0 0 ${space[4]};
  font-family: ${font.display};
  font-size: clamp(1.125rem, 1rem + 0.7vw, 1.6rem);
  line-height: ${leading.snug};
  color: ${color.onDark};
`;

const Intro = styled.p`
  max-width: 58ch;
  margin: 0 0 ${space[7]};
  font-size: ${type.base};
  line-height: ${leading.relaxed};
  color: ${color.onDarkMuted};

  ${media.md`
    font-size: ${type.sm};
    margin-bottom: ${space[6]};
  `}
`;

const Ways = styled.nav`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${space[3]};

  ${media.md`
    grid-template-columns: 1fr;
    gap: 10px;
  `}
`;

const Way = styled.a`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[4]};
  border: 1px solid rgba(246, 244, 238, 0.26);
  border-radius: ${radius.md};
  background: rgba(20, 21, 15, 0.34);
  backdrop-filter: blur(6px);
  color: ${color.onDark};
  transition:
    background ${motion.base},
    border-color ${motion.base},
    transform ${motion.base};

  &:hover {
    background: rgba(20, 21, 15, 0.55);
    border-color: ${color.onDark};
    transform: translateY(-2px);
  }

  strong {
    display: block;
    font-size: ${type.base};
    font-weight: ${weight.semibold};
    margin-bottom: 2px;
  }

  small {
    font-size: ${type.xs};
    line-height: ${leading.normal};
    color: ${color.onDarkMuted};
  }

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

const Foot = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space[4]};
  margin-top: ${space[7]};
  padding-top: ${space[5]};
  border-top: 1px solid rgba(246, 244, 238, 0.18);
  font-size: ${type.sm};
  color: ${color.onDarkMuted};
`;

const FootLinks = styled.div`
  display: flex;
  gap: ${space[5]};

  a:hover {
    color: ${color.onDark};
  }
`;

/* Highlights the trailing "NMC", the one piece of brand expression the page has without
 * photography of its own. */
const renderWordmark = (name) => {
  const at = name.lastIndexOf("NMC");
  if (at < 0) return name;
  return (
    <>
      {name.slice(0, at)}
      <span>{name.slice(at)}</span>
    </>
  );
};

const Identity = (props) => {
  const { locale, identity } = usePage().props;
  const language = locale === "id" ? "id" : "en";
  const copy = COPY[language];
  const prefix = language === "id" ? "/id" : "";
  const reducedMotion = useReducedMotion();
  const videoRef = useRef(null);

  /* Playback starts here rather than via an autoPlay attribute: the server cannot know the motion
   * preference, so the attribute would either lie or break hydration. A muted programmatic play()
   * is permitted by every autoplay policy, and with JS off the poster simply stays — which is the
   * correct outcome, not a degraded one. */
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;

    if (reducedMotion) {
      element.pause();
      return;
    }
    const attempt = element.play();
    if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
  }, [reducedMotion]);

  const ways = [
    {
      key: "property",
      href: propertyHome(language),
      copy: copy.actions.property,
      inertia: true,
    },
    // Deliberately a plain anchor: it redirects into the HGS app, outside these bilingual routes.
    {
      key: "guest",
      href: guestPortal(),
      copy: copy.actions.guest,
      rel: "nofollow",
    },
    {
      key: "insights",
      href: insightsHome(language),
      copy: copy.actions.insights,
      inertia: true,
    },
  ];

  return (
    <>
      <PageStyle />
      <SkipLink href="#main">{copy.skip}</SkipLink>

      <Screen as="main" id="main">
        <Backdrop>
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="metadata"
            poster="/video/brand-loop-poster.jpg"
            tabIndex={-1}
            aria-hidden="true"
          >
            {/* AV1 first for the browsers that have it, h.264 for everything else. */}
            <source
              src="/video/brand-loop.av1.mp4"
              type='video/mp4; codecs="av01.0.05M.08"'
            />
            <source src="/video/brand-loop.mp4" type="video/mp4" />
          </video>
        </Backdrop>
        <Scrim />

        <Inner>
          <Eyebrow>{copy.eyebrow}</Eyebrow>
          <Wordmark>
            {renderWordmark(identity?.brand_name || "HostelNMC")}
          </Wordmark>
          <Tagline>{identity?.tagline || copy.tagline}</Tagline>
          <Intro>{copy.intro}</Intro>

          <Ways aria-label={copy.navLabel}>
            {ways.map((way) => (
              <Way
                key={way.key}
                as={way.inertia ? Link : "a"}
                href={way.href}
                rel={way.rel}
              >
                <span>
                  <strong>{way.copy.title}</strong>
                  <small>{way.copy.body}</small>
                </span>
                <ArrowRight size={17} aria-hidden="true" />
              </Way>
            ))}
          </Ways>

          <Foot>
            <LanguageSwitch tone="dark" drop="up" />
            <FootLinks>
              <Link href={`${prefix}/hebe/privacy-policy`}>{copy.privacy}</Link>
              <Link href={`${prefix}/hebe/terms-of-service`}>{copy.terms}</Link>
            </FootLinks>
          </Foot>
        </Inner>
      </Screen>
    </>
  );
};

export default Identity;
